import { useEffect, useState } from "react";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import { canonical, collectionPageSchema } from "@/utils/seo";
import { getAllLocations } from "@/api/content";
import type { Location } from "@/types/content";

export default function LocationsPage({ initialData }: { initialData?: Location[] }) {
  const [locations, setLocations] = useState<Location[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData || initialData.length === 0);

  useEffect(() => {
    getAllLocations().then((data) => {
      setLocations(data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
      <div className="max-w-6xl mx-auto">
        <SEO
          meta={{
            title: "探索城市｜美好時光指南",
            description: "從台北到高雄，發現每座城市最棒的活動與體驗，瀏覽各城市精選店家與文章。",
            canonical: canonical("/locations"),
            keywords: ["城市探索", "台北活動", "高雄活動", "台中活動", "美好時光指南"],
            structuredData: collectionPageSchema("探索城市｜美好時光指南", "從台北到高雄，發現每座城市最棒的活動與體驗。", "/locations"),
          }}
        />
        <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
          探索城市
        </h1>
        <p className="text-slate-500 mb-10 max-w-2xl">
          從台北到高雄，發現每座城市最棒的活動與體驗
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((loc) => (
            <a
              key={loc.id}
              href={`/locations/${loc.slug}`}
              className="group block relative rounded-xl overflow-hidden aspect-[16/10]"
            >
              <img
                loading="lazy"
                src={loc.coverImage}
                alt={loc.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
              <div className="absolute inset-0 flex flex-col justify-end p-5">
                <h2 className="text-2xl font-bold text-white mb-1">
                  {loc.name}
                </h2>
                <p className="text-sm text-white/80 line-clamp-2">
                  {loc.description}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}